import { Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { AccountSelect } from '../../components/AccountSelect';
import { CheckOutSummary } from '../../components/CheckOutSummary';
import { GetBuyerAccounts } from '../../components/GetBuyerAccounts';
import PageLayout from '../../components/PageLayout';
import { Account, CheckOut } from '../../types';

const useStyles: any = makeStyles((theme: any) => ({
  confirmContainer: {
    margin: theme.spacing(2, 4),
  },
  bottomContainer: {
    borderTop: `1px solid ${theme.palette.grey['300']}`,
    backgroundColor: theme.palette.grey['100'],
    paddingTop: theme.spacing(2),
  },
}));

type AccountSelectionProps = {
  accounts: Account[];
  selectedAccount: Account;
  checkOut: CheckOut;
  isSubmitting: boolean;
  getAccounts: (productCode: string) => Promise<any>;
  getSupplierBuyerAssociation: (checkOutId: string, buyerAccountId: string) => Promise<any>;
  confirmPay: (checkOutId: string, buyerAccountId: string) => Promise<any>;
  setSelectedAccount: (account: Account) => void;
};

export const filterAccounts = (accounts: Account[], checkOut: CheckOut) => {
  accounts.forEach((x: Account) => {
    if (x.availableBalance >= checkOut.amount) {
      x.hasBalanceToProcess = true;
    } else {
      x.hasBalanceToProcess = false;
    }
  });

  return accounts.filter((x: Account) => x.gcAccountId !== checkOut.merchantGcAccountId);
};

export const sortAccounts = (accounts: Account[]) => {
  return accounts.sort((a: Account, b: Account) => {
    if (a.name < b.name) {
      return -1;
    } else if (a.name > b.name) {
      return 1;
    }
    return 0;
  });
};

const AccountSelection = (props: AccountSelectionProps) => {
  const { accounts, checkOut, getAccounts, selectedAccount, setSelectedAccount, getSupplierBuyerAssociation } = props;
  const classes = useStyles();
  const history = useHistory();
  const [buyerAccounts, setBuyerAccounts] = useState<Array<Account>>(accounts);

  useEffect(() => {
    if (checkOut && (!accounts || accounts.length === 0)) {
      getAccounts(checkOut.id).then((response: any) => {
        const { payload, error } = response;
        if (!error && payload.data.accounts) {
          const filteredAccounts = filterAccounts(payload.data.accounts, checkOut);
          const sortedAccounts: Account[] = sortAccounts(filteredAccounts);
          setBuyerAccounts(sortedAccounts);
          if (sortedAccounts.length === 0) {
            history.push({
              pathname: '/500',
              state: { message: 'No accounts to complete payment' },
            });
          }
        } else {
          history.push({
            pathname: '/500',
            state: { message: 'No accounts retrieved to complete payment' },
          });
        }
      });
    }
  }, [accounts]);

  const handleContinue = () => {
    history.push({
      pathname: '/confirm-checkout',
    });
  };

  const handleAccountChange = (account: Account) => {
    setSelectedAccount(account);
    if (checkOut.productCode === 'ST') {
      getSupplierBuyerAssociation(checkOut.id, account.gcAccountId);
    }
  };

  return buyerAccounts && buyerAccounts.length > 1 ? (
    <PageLayout pageTitle='Confirm purchase'>
      <CheckOutSummary />
      <Grid className={classes.bottomContainer}>
        <Grid>
          <AccountSelect accounts={buyerAccounts} selectedAccount={selectedAccount} onChange={handleAccountChange} />
        </Grid>
        <Grid container>
          <Grid item xs={12} className={classes.confirmContainer}>
            <Button
              size='large'
              variant='contained'
              color='primary'
              type='button'
              disabled={!selectedAccount}
              fullWidth
              data-testid='uia-continue'
              onClick={() => handleContinue()}
            >
              Continue
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </PageLayout>
  ) : (
    <GetBuyerAccounts
      checkOut={checkOut}
      accounts={buyerAccounts}
      getAccounts={getAccounts}
      getSupplierBuyerAssociation={getSupplierBuyerAssociation}
      setSelectedAccount={setSelectedAccount}
    />
  );
};

export default AccountSelection;
